import React, { useState } from "react";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
} from "../ui/hover-card";
import { useDesktopsStore } from "@/dentlab/src/store/Desktops";
import { formatDistance } from "date-fns";
import { de } from "date-fns/locale";
import {
	CheckCircle2,
	Clock,
	Eye,
	Loader2,
	RefreshCw,
	XCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
	getPrintJobBlobUrl,
	getPrintJobFileName,
} from "./print-job-file-utils";
import { FileViewerModal } from "@/components/src/file-viewer";

interface PrintJobsHoverCardProps {
	children: React.ReactNode;
	onInteraction?: () => void;
}

export const PrintJobsHoverCard: React.FC<PrintJobsHoverCardProps> = ({
	children,
	onInteraction,
}) => {
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [viewerData, setViewerData] = useState<{
		url: string;
		fileName: string;
	} | null>(null);
	const printJobs = useDesktopsStore((state) => state.printJobs);
	const retryPrintJob = useDesktopsStore((state) => state.retryPrintJob);
	const jobs = printJobs.slice(0, 5);
	const hasMore = printJobs.length > 5;

	const handleNavigate = () => {
		onInteraction?.();
		setOpen(false);
		setTimeout(
			() => navigate("/organisation/administration/druckerauftraege"),
			100
		);
	};

	return (
		<>
			<HoverCard
				openDelay={200}
				closeDelay={100}
				open={open}
				onOpenChange={setOpen}
			>
				<HoverCardTrigger asChild>{children}</HoverCardTrigger>
				<HoverCardContent
					side="right"
					align="center"
					className="w-80 p-0 rounded-lg bg-slate-100"
				>
					<div className="flex items-center justify-between px-3 py-2 border-b border-white">
						<h3 className="text-md font-medium text-gray-900">
							Druckaufträge
						</h3>
					</div>
					<div className="max-h-[400px] overflow-y-auto">
						{jobs.length === 0 ? (
							<div className="px-3 py-4 text-sm text-gray-400 text-center">
								Keine Druckaufträge
							</div>
						) : (
							<div className="flex flex-col">
								{jobs.map((job) => (
									<div
										key={job.id}
										className="flex items-center gap-2 px-3 py-2 hover:bg-white/60 cursor-pointer transition-colors"
										onClick={handleNavigate}
									>
										<div className="flex-shrink-0">
											{job.status === "failed" ? (
												<XCircle className="h-3.5 w-3.5 text-red-500" />
											) : job.status === "processing" ? (
												<Loader2 className="h-3.5 w-3.5 text-blue-500 animate-spin" />
											) : job.status === "completed" ? (
												<CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
											) : (
												<Clock className="h-3.5 w-3.5 text-amber-500" />
											)}
										</div>
										<div className="flex-1 min-w-0">
											<div className="text-sm text-gray-800 truncate">
												{getPrintJobFileName(job.data)}
											</div>
											<div className="text-xs text-gray-400">
												{job.printer_name} ·{" "}
												{formatDistance(
													new Date(job.created_at),
													new Date(),
													{
														addSuffix: true,
														locale: de,
													}
												)}
											</div>
										</div>
										{job.status === "failed" && (
											<button
												onClick={(e) => {
													e.stopPropagation();
													retryPrintJob(job);
												}}
												className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-700 rounded transition-colors"
												aria-label="Druckauftrag wiederholen"
												data-testid={`print-job-retry-${job.id}`}
											>
												<RefreshCw className="h-3.5 w-3.5" />
											</button>
										)}
										<button
											onClick={(e) => {
												e.stopPropagation();
												const url = getPrintJobBlobUrl(
													job.data
												);
												if (url) {
													setOpen(false);
													setViewerData({
														url,
														fileName:
															getPrintJobFileName(
																job.data
															),
													});
												}
											}}
											className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-700 rounded transition-colors"
											aria-label="Dokument anzeigen"
											data-testid={`preview-view-file-${job.id}`}
										>
											<Eye className="h-3.5 w-3.5" />
										</button>
									</div>
								))}
							</div>
						)}
						{hasMore && (
							<button
								onClick={handleNavigate}
								className="w-full px-3 py-2 text-xs text-gray-500 hover:text-gray-700 hover:bg-white/60 text-center transition-colors border-t border-white"
								data-testid="print-jobs-see-more"
							>
								Alle anzeigen
							</button>
						)}
					</div>
				</HoverCardContent>
			</HoverCard>

			{viewerData && (
				<FileViewerModal
					fileIds={[]}
					isOpen={true}
					onClose={() => setViewerData(null)}
					blobUrl={viewerData.url}
					blobFileName={viewerData.fileName}
				/>
			)}
		</>
	);
};
